/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import { useAppSelector } from 'hooks/redux'
import React, { useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { formatCalendarDate } from 'helpers/dates'
import {
  useFlagNeedsWorkMutation,
  useGetChargeNeedsWorkQuery,
  useParkEraClaimMutation,
  useUpdateNeedsWorkMutation,
} from 'store/services/needsWork'
import { NeedsWorkItemDto, NeedsWorkReason } from 'store/types'
import { NEEDS_WORK_REASON_OPTIONS } from './needsWork'
import { OwnerOption, OwnerSelect } from './OwnerSelect'

// Where the item is raised from: the charge itself, a remit parked instead of
// posted, or an open item being handed on or re-dated.
export type NeedsWorkFlagTarget =
  | { kind: 'charge'; chargeId: string }
  | { kind: 'remit'; eraId: string; claimId: string; chargeId: string; chargeLabel?: string | null }
  | { kind: 'edit'; item: NeedsWorkItemDto }

type Props = {
  target: NeedsWorkFlagTarget
  onClose: () => void
  onDone?: () => void
}

const labelCss = { fontSize: '0.813rem', fontWeight: 600 }
const errorCss = { color: '#dc3545', fontSize: '0.75rem', marginTop: '0.2rem' }

const TITLES: Record<NeedsWorkFlagTarget['kind'], string> = {
  charge: 'Flag charge as Needs Work',
  remit: 'Park remit as Needs Work',
  edit: 'Edit Needs Work item',
}

export const NeedsWorkFlagModal: React.FC<Props> = ({ target, onClose, onDone }) => {
  const currentUserId = useAppSelector((state) => state.init.id)
  const profile = useAppSelector((state) => state.init.profile)
  const editing = target.kind === 'edit' ? target.item : null

  const [reason, setReason] = useState<NeedsWorkReason | ''>(editing?.reason ?? '')
  const [reasonDetail, setReasonDetail] = useState(editing?.reasonDetail ?? '')
  const [owner, setOwner] = useState<OwnerOption | null>(() => {
    if (editing?.owner) return { value: editing.owner.id, label: editing.owner.name }
    if (!currentUserId) return null

    return {
      value: Number(currentUserId),
      label: [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || 'Me',
    }
  })
  const [followUpDate, setFollowUpDate] = useState(editing?.followUpDate ?? '')
  const [note, setNote] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // What is already open on the charge, so a second item is raised on purpose
  // rather than as a duplicate of the first.
  const chargeId = target.kind === 'edit' ? null : target.chargeId
  const { data: chargeItems = [] } = useGetChargeNeedsWorkQuery(chargeId ?? skipToken)
  const alreadyOpen = chargeItems.filter((item) => item.status === 'OPEN')

  const [flag, { isLoading: flagging }] = useFlagNeedsWorkMutation()
  const [park, { isLoading: parking }] = useParkEraClaimMutation()
  const [update, { isLoading: updating }] = useUpdateNeedsWorkMutation()
  const busy = flagging || parking || updating

  const reasonInvalid = !reason
  const detailInvalid = reason === 'OTHER' && !reasonDetail.trim()
  const ownerInvalid = !owner

  const submit = async () => {
    setSubmitted(true)

    if (reasonInvalid || detailInvalid || ownerInvalid || !owner || !reason) {
      return
    }

    const fields = {
      reason,
      reasonDetail: reasonDetail.trim() || null,
      ownerId: owner.value,
      followUpDate: followUpDate || null,
    }

    try {
      if (target.kind === 'edit') {
        await update({ id: target.item.id, ...fields }).unwrap()
        Toast({ type: 'success', label: 'Needs Work item updated' })
      } else if (target.kind === 'remit') {
        await park({
          eraId: target.eraId,
          claimId: target.claimId,
          chargeId: target.chargeId,
          ...fields,
          note: note.trim() || null,
        }).unwrap()
        Toast({ type: 'success', label: 'Remit parked — the charge is on the Needs Work list' })
      } else {
        await flag({ chargeId: target.chargeId, ...fields, note: note.trim() || null }).unwrap()
        Toast({ type: 'success', label: 'Charge flagged as Needs Work' })
      }

      onDone?.()
      onClose()
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message || 'Could not save the Needs Work item'
      Toast({ type: 'error', label: message })
    }
  }

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: '1.1rem' }}>{TITLES[target.kind]}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {target.kind === 'remit' && (
          <p css={{ fontSize: '0.875rem', color: '#6c757d' }}>
            The remit leaves the ERA list and nothing is posted — no payment, transfer, write-off or
            patient responsibility. Its adjudication shows on the charge
            {target.chargeLabel ? ` ${target.chargeLabel}` : ''} until the item is resolved, when it is
            posted or dismissed.
          </p>
        )}
        {alreadyOpen.length > 0 && (
          <div
            data-testid="needs-work-already-open"
            css={{
              background: '#fff4e5',
              border: '1px solid #ffd8a8',
              borderRadius: 5,
              padding: '8px 12px',
              marginBottom: 16,
              fontSize: '0.813rem',
              color: '#6b3a00',
            }}>
            <div css={{ fontWeight: 600, marginBottom: 4 }}>Already open on this charge</div>
            {alreadyOpen.map((item) => (
              <div key={item.id}>
                {item.reasonLabel} — {item.owner?.name || 'no owner'}
                {item.followUpDate ? `, follow-up ${formatCalendarDate(item.followUpDate)}` : ''}
              </div>
            ))}
            <div css={{ marginTop: 4, color: '#8a5a1f' }}>
              Add another only for a different problem or owner — to hand an item over or re-date it, use Edit on it.
            </div>
          </div>
        )}
        <div className="form-group">
          <label css={labelCss}>Reason</label>
          <select
            className="form-control"
            value={reason}
            disabled={busy}
            onChange={(event) => setReason(event.target.value as NeedsWorkReason)}
            css={submitted && reasonInvalid ? { borderColor: '#dc3545' } : undefined}>
            <option value="">Reason…</option>
            {NEEDS_WORK_REASON_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {submitted && reasonInvalid && <div css={errorCss}>A reason is required</div>}
        </div>
        <div className="form-group">
          <label css={labelCss}>{reason === 'OTHER' ? 'Describe the reason' : 'Details (optional)'}</label>
          <input
            className="form-control"
            maxLength={200}
            value={reasonDetail}
            disabled={busy}
            placeholder={reason === 'OTHER' ? 'What needs to be done' : 'e.g. Cigna processed at the OON rate'}
            onChange={(event) => setReasonDetail(event.target.value)}
            css={submitted && detailInvalid ? { borderColor: '#dc3545' } : undefined}
          />
          {submitted && detailInvalid && <div css={errorCss}>Describe the reason when it is not on the list</div>}
        </div>
        <div className="form-row">
          <div className="form-group col-md-7">
            <label css={labelCss}>Owner</label>
            <OwnerSelect value={owner} onChange={setOwner} invalid={submitted && ownerInvalid} disabled={busy} />
            {submitted && ownerInvalid && <div css={errorCss}>An owner is required</div>}
          </div>
          <div className="form-group col-md-5">
            <label css={labelCss}>Follow-up date (optional)</label>
            <input
              type="date"
              className="form-control"
              value={followUpDate}
              disabled={busy}
              onChange={(event) => setFollowUpDate(event.target.value)}
            />
          </div>
        </div>
        {target.kind !== 'edit' && (
          <div className="form-group mb-0">
            <label css={labelCss}>Note (optional)</label>
            <textarea
              className="form-control"
              rows={3}
              maxLength={1000}
              value={note}
              disabled={busy}
              placeholder="What was found, who to call, what to ask"
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" disabled={busy} onClick={submit}>
          {busy ? 'Saving…' : target.kind === 'remit' ? 'Park remit' : target.kind === 'edit' ? 'Save' : 'Flag'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
